import { Component, OnDestroy, OnInit } from '@angular/core';
import { ElementModel } from '../../../models/element.model';
import { DataService } from '../../../services/data.service';
import { ActivatedRoute } from '@angular/router';
import { AcModel } from '../../../models/ac.model';
import { AuthStateService } from '../../../services/auth-state.service';
import {
  faTrafficLightStop,
  faFolderOpen,
} from '@fortawesome/pro-light-svg-icons';
import { Subscription } from 'rxjs';
import { SlotModel } from '../../../models/slot.model';
import { UiService } from '../../../services/ui.service';
import { TranslocoService } from '@ngneat/transloco';
import { AppService } from '../../../services/app.service';
import { SelectItem } from 'primeng/api';
import { CandidateModel } from '../../../models/candidate.model';
import { GroupModel } from '../../../models/group.model';

@Component({
  selector: 'app-exercise',
  templateUrl: './exercise.component.html',
  styleUrls: ['./exercise.component.scss']
})
export class ExerciseComponent implements OnInit, OnDestroy {

  private polling: Subscription = null;

  public ac: AcModel = null;
  public acSubscription: Subscription = null;
  public slot: SlotModel = null;
  public element: ElementModel = null;
  public elements: Array<ElementModel> = [];
  public tabs: Array<SelectItem> = [];
  public activeTab: SelectItem = null;
  public candidate: CandidateModel = null;
  public group: GroupModel = null;

  private slotId: number = null;
  private elementId: number = null;
  private groupHash: string = null;
  private candidateHash: string = null;

  public faTrafficLightStop = faTrafficLightStop;
  public faFolderOpen = faFolderOpen;

  constructor(
    private route: ActivatedRoute,
    private app: AppService,
    private data: DataService,
    private ui: UiService,
    private transloco: TranslocoService,
    public auth: AuthStateService
  ) { }

  ngOnInit(): void {
    this.ui.setPageTitle(this.transloco.translate('common.labels.exercise'));
    this.acSubscription = this.data.ac$.subscribe({
      next: (ac: AcModel) => {
        if (ac !== null) {
          this.ac = ac;
          this.data.setActiveNav('exercise');
          this.initElements();
        }
      }
    });
    this.route.params.subscribe((params) => {
      this.slotId = parseInt(params.slot, 10) || null;
      this.elementId = parseInt(params.element, 10) || null;
      this.groupHash = params.group || null;
      this.candidateHash = params.candidate || null;
      this.initElements();
    });
  }

  ngOnDestroy(): void {
    if (this.polling !== null) {
      this.polling.unsubscribe();
      this.polling = null;
    }
    if (this.acSubscription !== null) {
      this.acSubscription.unsubscribe();
      this.acSubscription = null;
    }
  }

  public isEntitled(element: ElementModel = null): boolean {
    if (element === null) {
      element = this.element;
    }
    return element !== null &&
      (
        (this.auth.isObserver() && element.permissions.indexOf('observer') !== -1) ||
        (this.auth.isCandidate() && element.permissions.indexOf('candidate') !== -1) ||
        (this.auth.isModerator() && element.permissions.indexOf('moderator') !== -1)
      );
  }

  public onTabChanged(tab: SelectItem): void {
    this.activeTab = tab;
    this.element = this.elements.find(element => element.id === tab.value);
  }

  private initElements(): void {
    this.element = null;
    this.tabs = [];
    if (this.ac && this.slotId) {
      // load exercise
      this.slot = this.ac.getSlotOnId(this.slotId);
      if (this.slot !== null) {
        // set group and candidate
        if (this.groupHash !== null) {
          this.group = this.slot.exercise?.groups.find(group => group.hash === this.groupHash) || null;
        }
        if (this.candidateHash !== null) {
          this.candidate = this.ac?.candidates.find(candidate => candidate.hashed_id === this.candidateHash) || null;
        }
        const hash = this.data.getHash(this.slot, this.candidate, this.group);
        if (!this.auth.canOpenSlot(hash, this.slot, this.group, this.ac?.isWithinObserverOpenableTime())) {
          this.app.redirectToDashboard();
        } else {
          this.data.setActiveExercise(this.slot.id);
          // load elements
          this.elements = this.slot.elements.filter((element: ElementModel) => {
            return this.isEntitled(element);
          });
          if (this.elements.length > 0) {
            this.element = this.elements.find((element: ElementModel) => {
              return element.id === this.elementId;
            }) || this.elements[0] || null;
            this.tabs = this.elements.sort((a: ElementModel, b: ElementModel) => {
              return a.position - b.position;
            }).map((element: ElementModel) => {
              return {
                value: element.id,
                label: element.title || element.name
              };
            });
          }
          if (this.element) {
            this.data.setActiveElement(this.element?.id || null);
            this.activeTab = this.tabs.find((tab: SelectItem) => {
              return tab.value === this.element.id;
            });
          }
        }
      } else {
        this.app.redirectToDashboard();
      }
    }
  }

}
